import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, ArrowLeft, Loader2, Volume2, VolumeX, ArrowRight, Trophy, RotateCcw, Users2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { countFillerWords, calcWPM, speakText, stopSpeaking } from "@/lib/speechMetrics";
import WaveformVisualizer from "@/components/interview/WaveformVisualizer";
import AnswerScoreCard from "@/components/interview/AnswerScoreCard";
import SqlSandboxPanel from "@/components/interview/SqlSandboxPanel";
import FinalScorecard from "@/components/interview/FinalScorecard";
import AvatarQuestionPlayer from "@/components/interview/AvatarQuestionPlayer";
import ProctoringConsentScreen from "@/components/interview/ProctoringConsentScreen";
import ProctoringStatusBar from "@/components/interview/ProctoringStatusBar";
import QuestionTimer from "@/components/interview/QuestionTimer";
import { useProctoring } from "@/hooks/useProctoring";
import { fireConfetti } from "@/lib/confetti";

const ACCENT_BY_TRACK = { data_cloud: "indigo", sfmc: "teal", mc_next: "fuchsia", servicenow: "emerald" };
const ACCENT_BTN = { indigo: "bg-primary hover:bg-primary/90", teal: "bg-teal-500 hover:bg-teal-400", fuchsia: "bg-fuchsia-500 hover:bg-fuchsia-400" };
const ACCENT_TEXT = { indigo: "text-indigo-400", teal: "text-teal-400", fuchsia: "text-fuchsia-400" };
const DIFFICULTY_LABELS = { 1: "Beginner", 2: "Easy", 3: "Intermediate", 4: "Advanced", 5: "Expert" };

export default function VoiceInterviewPage() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const speech = useSpeechRecognition();

  const [meta, setMeta] = useState(location.state || null);
  const [loading, setLoading] = useState(!location.state);
  const [phase, setPhase] = useState("asking"); // asking | submitting | reviewing | complete
  const [manualAnswer, setManualAnswer] = useState("");
  const [sqlValue, setSqlValue] = useState("");
  const [sqlResult, setSqlResult] = useState(null);
  const [sqlRunning, setSqlRunning] = useState(false);
  const [lastReview, setLastReview] = useState(null);
  const [pendingNext, setPendingNext] = useState(null);
  const [finalScorecard, setFinalScorecard] = useState(null);
  const [muted, setMuted] = useState(false);
  const spokenForQuestionRef = useRef(null);
  const proctoring = useProctoring({ sessionId, sessionType: "live", onTerminate: () => navigate("/interview") });

  const accent = ACCENT_BY_TRACK[meta?.track] || "indigo";

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get(`/voice-interview/${sessionId}`);
      const pending = data.turns.find((t) => !t.answered);
      const answeredCount = data.turns.filter((t) => t.answered).length;
      setMeta({
        session_id: data.id, type: data.type, track: data.track, difficulty_level: data.difficulty_level,
        question: pending?.question, focus_area: pending?.focus_area,
        question_number: answeredCount + 1, max_questions: data.max_questions,
        sql_dataset: data.type === "sql" ? true : null,
        mode: data.mode, round: pending?.round, timed_mode: data.timed_mode, time_limit_seconds: data.time_limit_seconds,
      });
      if (data.status === "completed") {
        setFinalScorecard(data.scorecard);
        setPhase("complete");
      }
    } catch (e) {
      toast.error("Couldn't load this interview session");
      navigate("/interview");
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!location.state) loadSession();
  }, [location.state, loadSession]);

  useEffect(() => {
    if (!meta?.question || phase !== "asking" || muted) return;
    if (spokenForQuestionRef.current === meta.question) return;
    spokenForQuestionRef.current = meta.question;
    speakText(meta.question);
    return () => stopSpeaking();
  }, [meta?.question, phase, muted]);

  const lastRoundRef = useRef(null);
  useEffect(() => {
    if (meta?.round && meta.round !== lastRoundRef.current) {
      lastRoundRef.current = meta.round;
      toast.info(`Now entering: ${meta.round}`);
    }
  }, [meta?.round]);

  const handleAutoStop = (finalText) => {
    setManualAnswer(finalText);
  };

  const runSqlQuery = async () => {
    setSqlRunning(true);
    try {
      const { data } = await api.post(`/voice-interview/${sessionId}/sql/execute`, { query: sqlValue });
      setSqlResult(data);
    } catch (e) {
      toast.error("Couldn't run this query");
    } finally {
      setSqlRunning(false);
    }
  };

  const submitAnswer = async (isTimeout = false) => {
    const isSql = meta.type === "sql";
    let answerText = isSql ? manualAnswer.trim() : (manualAnswer || speech.transcript).trim();
    if (isTimeout && !answerText) answerText = "(No answer provided — time expired)";
    if (isSql && !sqlValue.trim() && !isTimeout) {
      toast.error("Write a SQL query before submitting");
      return;
    }
    if (!isSql && !answerText) {
      toast.error("Record or type your answer before submitting");
      return;
    }
    if (speech.isListening) speech.stop();

    setPhase("submitting");
    const duration = speech.getDurationSeconds();
    const payload = isSql
      ? { answer_text: answerText || "See SQL query and result.", sql_query: sqlValue, sql_result: sqlResult, filler_word_count: 0, wpm: 0, duration_seconds: 0 }
      : { answer_text: answerText, filler_word_count: countFillerWords(answerText), wpm: calcWPM(answerText, duration), duration_seconds: duration };

    try {
      const { data } = await api.post(`/voice-interview/${sessionId}/answer`, payload);
      setLastReview({ scores: data.answer_scores, idealAnswer: data.ideal_answer, feedback: data.feedback, fillerWordCount: payload.filler_word_count, wpm: payload.wpm });
      setMeta((m) => ({ ...m, difficulty_level: data.difficulty_level }));
      if (data.is_complete) {
        setFinalScorecard(data.scorecard);
        setPendingNext(null);
      } else {
        setPendingNext(data.next_question);
      }
      setPhase("reviewing");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't submit your answer");
      setPhase("asking");
    }
  };

  const handleTimeExpire = () => {
    if (phase === "asking") submitAnswer(true);
  };

  const handleContinue = () => {
    setManualAnswer("");
    setSqlValue("");
    setSqlResult(null);
    setLastReview(null);
    if (pendingNext) {
      setMeta((m) => ({ ...m, question: pendingNext.question, focus_area: pendingNext.focus_area, question_number: pendingNext.question_number, round: pendingNext.round }));
      setPhase("asking");
    } else {
      fireConfetti();
      setPhase("complete");
    }
  };

  useEffect(() => {
    if (phase === "complete") proctoring.finalizeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (loading || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="voice-interview-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (phase !== "complete" && !proctoring.consentGiven) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <ProctoringConsentScreen starting={proctoring.starting} error={proctoring.error} onConsent={proctoring.requestConsent} />
      </div>
    );
  }

  const btnClass = ACCENT_BTN[accent];
  const textClass = ACCENT_TEXT[accent];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="voice-interview-page">
      {phase !== "complete" && (
        <ProctoringStatusBar webcamVideoRef={proctoring.webcamVideoRef} violationCount={proctoring.violationCount} violationLimit={proctoring.violationLimit} />
      )}
      <button onClick={() => navigate("/interview")} data-testid="voice-interview-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> End Interview
      </button>

      {phase === "complete" && finalScorecard ? (
        <>
          <h1 className="font-heading text-2xl font-semibold text-white mb-5">Interview Complete</h1>
          <FinalScorecard scorecard={finalScorecard} accent={accent} />
          <div className="flex items-center gap-3 mt-6">
            <button onClick={() => navigate("/interview")} data-testid="voice-interview-back-to-center-btn" className={`flex items-center gap-2 ${btnClass} text-white text-sm font-semibold px-5 py-2.5 rounded-lg`}>
              <ArrowLeft className="w-4 h-4" /> Back to Interview Center
            </button>
            <button onClick={() => navigate(`/interview?track=${meta.track}`)} data-testid="voice-interview-practice-again-btn" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-5 py-2.5 rounded-lg">
              <RotateCcw className="w-4 h-4" /> Practice Again
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <span className={`text-xs font-bold uppercase tracking-[0.15em] ${textClass}`} data-testid="voice-interview-type-badge">{meta.type} Interview</span>
            <span className="text-xs font-semibold bg-white/5 px-2.5 py-1 rounded-full text-slate-300" data-testid="voice-interview-difficulty-badge">{DIFFICULTY_LABELS[meta.difficulty_level] || "Intermediate"}</span>
            {meta.round && (
              <span className="flex items-center gap-1 text-xs font-semibold bg-indigo-400/15 text-indigo-300 px-2.5 py-1 rounded-full" data-testid="voice-interview-round-badge">
                <Users2 className="w-3.5 h-3.5" /> {meta.round}
              </span>
            )}
            <span className="text-xs text-slate-500">Question {meta.question_number} of ~{meta.max_questions}</span>
            {meta.timed_mode && phase === "asking" && (
              <QuestionTimer key={meta.question_number} seconds={meta.time_limit_seconds} onExpire={handleTimeExpire} />
            )}
            <button onClick={() => setMuted((m) => !m)} data-testid="voice-interview-mute-btn" className="ml-auto text-slate-400 hover:text-white">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>

          <div className="glass-panel rounded-2xl p-6 mb-5" data-testid="voice-interview-question-card">
            <AvatarQuestionPlayer questionText={meta.question} accent={accent} />
            <p className="text-xs font-semibold text-slate-500 mb-2">{meta.focus_area}</p>
            <p className="text-lg text-white font-medium leading-relaxed">{meta.question}</p>
          </div>

          {phase === "reviewing" && lastReview ? (
            <div className="space-y-5">
              <AnswerScoreCard {...lastReview} accent={accent} />
              <button onClick={handleContinue} data-testid="voice-interview-continue-btn" className={`w-full flex items-center justify-center gap-2 ${btnClass} text-white font-semibold py-3 rounded-lg`}>
                {pendingNext ? "Next Question" : "See Final Scorecard"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {meta.type === "sql" && (
                <>
                  <SqlSandboxPanel value={sqlValue} onChange={setSqlValue} onRun={runSqlQuery} running={sqlRunning} result={sqlResult} accent={accent} />
                  <textarea
                    value={manualAnswer} onChange={(e) => setManualAnswer(e.target.value)} rows={2}
                    placeholder="Briefly explain your query approach (optional)..." data-testid="voice-interview-sql-explanation-textarea"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
                  />
                </>
              )}

              {meta.type !== "sql" && speech.supported && (
                <div className="glass-panel rounded-2xl p-6 flex flex-col items-center gap-4" data-testid="voice-interview-mic-panel">
                  <WaveformVisualizer level={speech.audioLevel} active={speech.isListening} accent={accent} />
                  <button
                    onClick={() => (speech.isListening ? speech.stop() : speech.start(handleAutoStop))}
                    data-testid="voice-interview-mic-btn"
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${speech.isListening ? "bg-rose-500 hover:bg-rose-400" : btnClass}`}
                  >
                    {speech.isListening ? <Square className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
                  </button>
                  <p className="text-xs text-slate-400">{speech.isListening ? "Listening — pause when you're done" : "Tap to answer by voice"}</p>
                  {(speech.transcript || speech.interimTranscript) && (
                    <p className="text-sm text-slate-300 text-center" data-testid="voice-interview-live-transcript">
                      {speech.transcript} <span className="text-slate-500">{speech.interimTranscript}</span>
                    </p>
                  )}
                </div>
              )}

              {(meta.type !== "sql" && !speech.supported) && (
                <textarea
                  value={manualAnswer} onChange={(e) => setManualAnswer(e.target.value)} rows={5}
                  placeholder="Your browser doesn't support voice input — type your answer here..." data-testid="voice-interview-manual-textarea"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500"
                />
              )}

              <button
                onClick={submitAnswer} disabled={phase === "submitting"} data-testid="voice-interview-submit-btn"
                className={`w-full flex items-center justify-center gap-2 ${btnClass} disabled:opacity-60 text-white font-semibold py-3 rounded-lg`}
              >
                {phase === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                {phase === "submitting" ? "AI is evaluating…" : "Submit Answer"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
